import { createSlice } from '@reduxjs/toolkit';
import {
  fetchOpportunitiesData,
  fetchFrequencyChangeData,
  fetchInjectionRateIncreaseData,
  fetchTrackedChangesFRQ,
  fetchESPPumpChangeData,
  fetchTrackedChangesIPI,
  fetchInjectionRateReductionData,
  fetchWHPReductionData,
  fetchDepthChangeData,
} from '../../services/recommendations/RecommendationsService';

const initialState = {
  opportunities: {
    data: undefined,
    loading: false,
    error: undefined,
  },
  frequencyChange: {
    data: undefined,
    loading: false,
    error: undefined,
  },
  injectionRateIncrease: {
    data: undefined,
    loading: false,
    error: undefined,
  },
  trackedChangesFRQ: {
    data: undefined,
    loading: false,
    error: undefined,
  },
  espPumpChange: {
    data: undefined,
    loading: false,
    error: undefined,
  },
  trackedChangesIPI: {
    data: undefined,
    loading: false,
    error: undefined,
  },
  injectionRateReduction: {
    data: undefined,
    loading: false,
    error: undefined,
  },
  whpReduction: {
    data: undefined,
    loading: false,
    error: undefined,
  },
  depthChange: {
    data: undefined,
    loading: false,
    error: undefined,
  },
  chartActiveTab: 'ESP',
};

const RecommendationsSlice = createSlice({
  name: 'recommendations',
  initialState,
  reducers: {
    setInitialState: (state) => {
      state.opportunities = { data: undefined, error: undefined, loading: false };
      state.frequencyChange = { data: undefined, error: undefined, loading: false };
      state.injectionRateIncrease = { data: undefined, error: undefined, loading: false };
      state.trackedChangesFRQ = { data: undefined, error: undefined, loading: false };
      state.espPumpChange = { data: undefined, error: undefined, loading: false };
      state.trackedChangesIPI = { data: undefined, error: undefined, loading: false };
      state.injectionRateReduction = { data: undefined, error: undefined, loading: false };
      state.whpReduction = { data: undefined, error: undefined, loading: false };
      state.depthChange = { data: undefined, error: undefined, loading: false };
      state.chartActiveTab = 'ESP';
    },
    setChartActiveTab: (state, action) => {
      state.chartActiveTab = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Opportunities data
      .addCase(fetchOpportunitiesData.pending, (state) => {
        state.opportunities.loading = true;
      })
      .addCase(fetchOpportunitiesData.fulfilled, (state, action) => {
        state.opportunities.loading = false;
        state.opportunities.data = action.payload;
      })
      .addCase(fetchOpportunitiesData.rejected, (state) => {
        state.opportunities.loading = false;
      })
      // Frequency change data
      .addCase(fetchFrequencyChangeData.pending, (state) => {
        state.frequencyChange.loading = true;
      })
      .addCase(fetchFrequencyChangeData.fulfilled, (state, action) => {
        state.frequencyChange.loading = false;
        state.frequencyChange.data = action.payload;
      })
      .addCase(fetchFrequencyChangeData.rejected, (state) => {
        state.frequencyChange.loading = false;
      })
      // Tracked frequency changes data
      .addCase(fetchTrackedChangesFRQ.pending, (state) => {
        state.trackedChangesFRQ.loading = true;
      })
      .addCase(fetchTrackedChangesFRQ.fulfilled, (state, action) => {
        state.trackedChangesFRQ.loading = false;
        state.trackedChangesFRQ.data = action.payload;
      })
      .addCase(fetchTrackedChangesFRQ.rejected, (state) => {
        state.trackedChangesFRQ.loading = false;
      })
      // ESP pump change data
      .addCase(fetchESPPumpChangeData.pending, (state) => {
        state.espPumpChange.loading = true;
      })
      .addCase(fetchESPPumpChangeData.fulfilled, (state, action) => {
        state.espPumpChange.loading = false;
        state.espPumpChange.data = action.payload;
      })
      .addCase(fetchESPPumpChangeData.rejected, (state) => {
        state.espPumpChange.loading = false;
      })
      // Injection rate increase data
      .addCase(fetchInjectionRateIncreaseData.pending, (state) => {
        state.injectionRateIncrease.loading = true;
      })
      .addCase(fetchInjectionRateIncreaseData.fulfilled, (state, action) => {
        state.injectionRateIncrease.loading = false;
        state.injectionRateIncrease.data = action.payload;
      })
      .addCase(fetchInjectionRateIncreaseData.rejected, (state) => {
        state.injectionRateIncrease.loading = false;
      })
      // Tracked Injection Gas Rate changes data
      .addCase(fetchTrackedChangesIPI.pending, (state) => {
        state.trackedChangesIPI.loading = true;
      })
      .addCase(fetchTrackedChangesIPI.fulfilled, (state, action) => {
        state.trackedChangesIPI.loading = false;
        state.trackedChangesIPI.data = action.payload;
      })
      .addCase(fetchTrackedChangesIPI.rejected, (state) => {
        state.trackedChangesIPI.loading = false;
      })
      // Injection rate reduction data
      .addCase(fetchInjectionRateReductionData.pending, (state) => {
        state.injectionRateReduction.loading = true;
      })
      .addCase(fetchInjectionRateReductionData.fulfilled, (state, action) => {
        state.injectionRateReduction.loading = false;
        state.injectionRateReduction.data = action.payload;
      })
      .addCase(fetchInjectionRateReductionData.rejected, (state) => {
        state.injectionRateReduction.loading = false;
      })
      // WHP reduction data
      .addCase(fetchWHPReductionData.pending, (state) => {
        state.whpReduction.loading = true;
      })
      .addCase(fetchWHPReductionData.fulfilled, (state, action) => {
        state.whpReduction.loading = false;
        state.whpReduction.data = action.payload;
      })
      .addCase(fetchWHPReductionData.rejected, (state) => {
        state.whpReduction.loading = false;
      })
      // Depth change data
      .addCase(fetchDepthChangeData.pending, (state) => {
        state.depthChange.loading = true;
      })
      .addCase(fetchDepthChangeData.fulfilled, (state, action) => {
        state.depthChange.loading = false;
        state.depthChange.data = action.payload;
      })
      .addCase(fetchDepthChangeData.rejected, (state) => {
        state.depthChange.loading = false;
      });
  },
});

export const { setInitialState, setChartActiveTab } = RecommendationsSlice.actions;
export default RecommendationsSlice.reducer;
